import { useQuery } from '@tanstack/react-query';
import {
  eisfPermissions,
  type EisfOperation,
  type EisfPermissionsResponse,
} from '@/api/client';
import { useMyPermissions } from '@/hooks/useMyPermissions';

/**
 * What the caller may do in the eISF binder.
 *
 * READS THE RBAC GRID, NOT THE SHIPPED MATRIX.
 * It used to call GET /eisf/permission-matrix, whose own handler says it
 * answers "what does the matrix say?", NOT "what may I actually do here?".
 * Every eISF HANDLER moved to the operation registry in batch 2, so the
 * buttons and the API had drifted apart: ticking eISF Binder → create in
 * /admin/rbac changed what the API accepted and nothing about what the UI
 * offered, and a role could be shown a button the server refuses — or, as
 * reported live, be refused a button the server would have allowed.
 *
 * The `can(EisfOperation)` signature is unchanged on purpose: six call sites
 * across the binder screens keep working untouched.
 *
 * THE MATRIX IS STILL FETCHED, as the fallback. Two operations have no
 * registry equivalent (configure_expiration_rules, grant_remote_access), and
 * the grid only answers for modules that are registered — so an unmapped
 * operation keeps its old answer rather than silently becoming false and
 * hiding a working button.
 */

/**
 * eISF operation -> registry operation key.
 *
 * `upload_document_version` maps to binder.upload, which was split out of
 * binder.update (2026-10-09) so "may file a document" can be granted without
 * "may re-tag, reassign and rename every document in the binder".
 *
 * Deliberately absent: `configure_expiration_rules` and `grant_remote_access`
 * have no operation yet, so they fall through to the matrix below.
 */
const TO_OPERATION: Partial<Record<EisfOperation, string>> = {
  view_binder_full: 'binder.read',
  view_binder_redacted: 'binder.read_redacted',
  download_documents: 'binder.download',
  create_document_slot: 'binder.create',
  create_or_rename_folder: 'binder.create',
  upload_document_version: 'binder.upload',
  edit_document_metadata: 'binder.update',
  start_approval_workflow: 'binder.approve',
  approve_document: 'binder.approve',
  initiate_esignature: 'binder.signature_sync',
  lock_or_unlock_document: 'binder.lock',
  checkout_document: 'binder.checkout',
  annotate_document: 'binder.annotate',
  bulk_export_binder: 'binder.bulk_export',
  manage_regulatory_tags: 'master_data.update',
  configure_binder_templates: 'master_data.binder_templates_write',
};

export interface UseEisfPermissionsResult {
  loading: boolean;
  data: EisfPermissionsResponse | null;
  can: (op: EisfOperation) => boolean;
}

export function useEisfPermissions(): UseEisfPermissionsResult {
  const perms = useMyPermissions();

  const q = useQuery({
    queryKey: ['eisf', 'permissions'],
    queryFn: eisfPermissions.get,
    staleTime: 5 * 60_000,
  });

  const fromMatrix = (op: EisfOperation): boolean =>
    Boolean(q.data?.your_permissions?.[op]);

  return {
    loading: q.isLoading || perms.isLoading,
    data: q.data ?? null,
    can: (op: EisfOperation): boolean => {
      const key = TO_OPERATION[op];
      // No registry equivalent, or the grid has not answered yet: keep the
      // old behaviour rather than flipping a button off under the user.
      if (!key || !perms.ready) return fromMatrix(op);
      const module = key.split('.')[0] ?? '';
      if (!perms.registeredModules.has(module)) return fromMatrix(op);
      return perms.holdsOperation(key);
    },
  };
}
